import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button/Button';
import { useProjectsStoreApi } from '../stores/projectsContext';
import { getRemixBundle } from './galleryApi';

/**
 * "Remix this" button — forks an approved gallery submission into
 * a brand-new local project on the kid's device.
 *
 * What carries over:
 *  - project name (with " (remix)" suffix so duplicates are obvious)
 *  - project kind
 *  - class labels (empty classes — no samples are copied)
 *  - block program (the kid's behavior, copied verbatim)
 *
 * What does NOT carry over:
 *  - sample bytes. Those were uploaded for moderation review and
 *    re-distributing them would re-expose another kid's data
 *    without their consent. The remixer captures fresh samples.
 *
 * The button is intentionally available without sign-in — remix is
 * a local-first action, mirroring how `Open project file` works.
 */
export function RemixButton({ submissionId }: { submissionId: string }): JSX.Element {
  const navigate = useNavigate();
  const store = useProjectsStoreApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onRemix = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const bundle = await getRemixBundle(submissionId);
      const project = await store.getState().remixFromBundle({
        project_name: bundle.project_name,
        project_kind: bundle.project_kind,
        block_program: bundle.block_program,
        class_labels: bundle.class_labels,
      });
      navigate(`/studio/p/${project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remix.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button onClick={() => void onRemix()} disabled={busy} variant="secondary">
        {busy ? 'Remixing…' : 'Remix this'}
      </Button>
      {error && (
        <p role="alert" className="bl-gallery__error">
          {error}
        </p>
      )}
    </>
  );
}
